import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  FolderOpen,
  Plus,
  Lock,
  Globe,
  MoreVertical,
  Pencil,
  Trash2,
  FileText,
  Loader2,
  Book,
  Code,
  HelpCircle,
  Lightbulb,
  Bookmark,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config';

const SPACE_ICONS = [
  { value: 'folder', icon: FolderOpen, label: 'Folder' },
  { value: 'book', icon: Book, label: 'Book' },
  { value: 'code', icon: Code, label: 'Code' },
  { value: 'file-text', icon: FileText, label: 'Document' },
  { value: 'help-circle', icon: HelpCircle, label: 'Help' },
  { value: 'lightbulb', icon: Lightbulb, label: 'Ideas' },
  { value: 'bookmark', icon: Bookmark, label: 'Bookmark' },
];

const getIconComponent = (iconName) => {
  const iconObj = SPACE_ICONS.find((i) => i.value === iconName);
  return iconObj ? iconObj.icon : FolderOpen;
};

const SpacesPage = () => {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editSpace, setEditSpace] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: 'folder', visibility: 'private' });
  const [submitting, setSubmitting] = useState(false);

  const canCreateSpace =
    currentOrg?.user_role && ['company', 'member'].includes(currentOrg.user_role);
  const canDeleteSpace = currentOrg?.user_role === 'company';

  const fetchSpaces = useCallback(async () => {
    if (!currentOrg) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/spaces`);
      setSpaces(response.data);
    } catch (error) {
      console.error('Failed to fetch spaces:', error);
      toast.error('Failed to load spaces');
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/organizations/${currentOrg.id}/spaces`, formData);
      setSpaces((prev) => [...prev, response.data]);
      setShowCreate(false);
      setFormData({ name: '', icon: 'folder', visibility: 'private' });
      toast.success('Space created successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create space';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !editSpace) return;

    setSubmitting(true);
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${currentOrg.id}/spaces/${editSpace.id}`,
        formData
      );
      setSpaces((prev) => prev.map((s) => (s.id === editSpace.id ? response.data : s)));
      setShowEdit(false);
      setEditSpace(null);
      setFormData({ name: '', icon: 'folder', visibility: 'private' });
      toast.success('Space updated successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update space';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (space) => {
    if (!window.confirm(`Delete "${space.name}"? All documents will be permanently removed.`)) return;

    try {
      await axios.delete(`${API_URL}/organizations/${currentOrg.id}/spaces/${space.id}`);
      setSpaces((prev) => prev.filter((s) => s.id !== space.id));
      toast.success('Space deleted');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete space';
      toast.error(message);
    }
  };

  const openEditDialog = (space) => {
    setEditSpace(space);
    setFormData({ name: space.name, icon: space.icon, visibility: space.visibility });
    setShowEdit(true);
  };

  const handleSpaceClick = (space) => {
    navigate(`/dashboard/spaces/${space.id}`);
  };

  if (!currentOrg) {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="spaces-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">Spaces</h1>
          <p className="text-slate-600 dark:text-slate-400">Select a workspace to view spaces</p>
        </div>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No Workspace Selected</h3>
            <p className="text-slate-600 dark:text-slate-400">Please select a workspace from the sidebar.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="spaces-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">Spaces</h1>
          <p className="text-slate-600 dark:text-slate-400">Organize your documentation into spaces</p>
        </div>
        {canCreateSpace && (
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setShowCreate(true)}
            data-testid="create-space-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Space
          </Button>
        )}
      </div>

      {/* Spaces Grid */}
      {loading ? (
        <div className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
        </div>
      ) : spaces.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {spaces.map((space) => {
            const IconComponent = getIconComponent(space.icon);
            return (
              <Card
                key={space.id}
                className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-blue-500/50 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => handleSpaceClick(space)}
                data-testid={`space-card-${space.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {space.name}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {space.visibility === 'public' ? (
                            <Badge className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 text-xs">
                              <Globe className="w-3 h-3 mr-1" />
                              Public
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 text-xs">
                              <Lock className="w-3 h-3 mr-1" />
                              Private
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {canCreateSpace && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(space);
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {canDeleteSpace && (
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(space);
                              }}
                              className="text-red-600 dark:text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <p className="text-sm text-slate-500 dark:text-slate-500">
                    {space.document_count} document{space.document_count !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No Spaces Yet</h3>
            <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
              Spaces help you organize your documentation by product, team, or topic.
            </p>
            {canCreateSpace ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setShowCreate(true)}
                data-testid="create-first-space-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Space
              </Button>
            ) : (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <Lock className="w-4 h-4" />
                <span>Contact an admin to create spaces</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Space Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Create Space</DialogTitle>
            <DialogDescription>Create a new space to organize your documentation.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="space-name">Name</Label>
                <Input
                  id="space-name"
                  placeholder="API Documentation"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="space-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex gap-2 flex-wrap">
                  {SPACE_ICONS.map(({ value, icon: Icon, label }) => (
                    <Button
                      key={value}
                      type="button"
                      variant={formData.icon === value ? 'default' : 'outline'}
                      size="icon"
                      className={formData.icon === value ? 'bg-blue-600 text-white' : ''}
                      onClick={() => setFormData({ ...formData, icon: value })}
                      title={label}
                    >
                      <Icon className="w-4 h-4" />
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="space-visibility">Visibility</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(v) => setFormData({ ...formData, visibility: v })}
                >
                  <SelectTrigger id="space-visibility" data-testid="space-visibility-select">
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Private - Team members only
                      </div>
                    </SelectItem>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Public - Anyone can view
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={submitting || !formData.name.trim()}
                data-testid="create-space-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Space'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Space Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Edit Space</DialogTitle>
            <DialogDescription>Update space settings.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-space-name">Name</Label>
                <Input
                  id="edit-space-name"
                  placeholder="API Documentation"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="edit-space-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex gap-2 flex-wrap">
                  {SPACE_ICONS.map(({ value, icon: Icon, label }) => (
                    <Button
                      key={value}
                      type="button"
                      variant={formData.icon === value ? 'default' : 'outline'}
                      size="icon"
                      className={formData.icon === value ? 'bg-blue-600 text-white' : ''}
                      onClick={() => setFormData({ ...formData, icon: value })}
                      title={label}
                    >
                      <Icon className="w-4 h-4" />
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-space-visibility">Visibility</Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(v) => setFormData({ ...formData, visibility: v })}
                >
                  <SelectTrigger id="edit-space-visibility">
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Private
                      </div>
                    </SelectItem>
                    <SelectItem value="public">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Public
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEdit(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={submitting || !formData.name.trim()}
                data-testid="edit-space-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpacesPage;
